import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { fmt } from "../lib/calc";
import { groupByJob } from "../lib/bank";
import {
  Archive, Upload, Loader2, Star, Trash2, Eye, X, ExternalLink,
  PackageCheck, Undo2, Layers,
} from "lucide-react";

export function DrawingBank() {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [tab, setTab] = useState("active");
  const [viewPanel, setViewPanel] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank");
      setPanels(data.panels || []);
    } catch {
      toast.error("Could not load the bank");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/bank/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const added = data.count || 0;
      if (added === 0) toast.info(data.duplicates ? "All panels already in the bank" : "No panels found in that PDF");
      else toast.success(`Added ${added} panel${added !== 1 ? "s" : ""} to the bank${data.duplicates ? ` (${data.duplicates} duplicate skipped)` : ""}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF import failed");
    } finally {
      setImporting(false);
    }
  };

  const togglePanel = async (p) => {
    setPanels((prev) => prev.map((x) => (x.id === p.id ? { ...x, priority: !x.priority } : x)));
    try {
      await api.patch(`/bank/${p.id}`, { priority: !p.priority });
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const toggleJob = async (job, status, makePriority) => {
    setPanels((prev) => prev.map((x) => (x.jobNumber === job && x.status === status ? { ...x, priority: makePriority } : x)));
    try {
      await api.post("/bank/job-priority", { jobNumber: job, status, priority: makePriority });
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const movePanel = async (p, status) => {
    setPanels((prev) => prev.map((x) => (x.id === p.id ? { ...x, status } : x)));
    try {
      await api.post("/bank/move", { ids: [p.id], status });
      toast.success(status === "completed" ? "Moved to Completed" : "Restored to Drawing Bank");
    } catch {
      toast.error("Move failed");
      load();
    }
  };

  const removePanel = async (p) => {
    setPanels((prev) => prev.filter((x) => x.id !== p.id));
    try {
      await api.delete(`/bank/${p.id}`);
    } catch {
      toast.error("Delete failed");
      load();
    }
  };

  const shown = panels.filter((p) => (p.status || "active") === tab);
  const groups = groupByJob(shown);
  const activeCount = panels.filter((p) => (p.status || "active") === "active").length;
  const completedCount = panels.filter((p) => p.status === "completed").length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Archive className="w-5 h-5 text-[#2560C9]" />
          <h1 className="font-heading font-black text-white text-lg tracking-tight">DRAWING BANK</h1>
          <span className="text-[10px] font-mono text-[#6E7787]">{panels.length} panels stored</span>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" onChange={handleFile} className="hidden" data-testid="bank-import-input" />
          <button
            data-testid="bank-import-button"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1.5 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-sm px-3 py-1.5 transition-colors disabled:opacity-60"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload Drawings
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 mb-4">
        <BankTab active={tab === "active"} onClick={() => setTab("active")} testid="bank-tab-active" icon={Layers}>
          Drawing Bank ({activeCount})
        </BankTab>
        <BankTab active={tab === "completed"} onClick={() => setTab("completed")} testid="bank-tab-completed" icon={PackageCheck}>
          Completed ({completedCount})
        </BankTab>
      </div>

      {loading ? (
        <div className="text-center text-[#6E7787] font-mono text-sm py-16">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading bank…
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center text-[#6E7787] text-sm font-mono py-16 border border-dashed border-[#2E3033]">
          {tab === "active" ? (
            <>No drawings yet. <span className="text-[#3B75DF]">Upload</span> a PDF to build your bank.</>
          ) : (
            <>No completed panels yet. Panels move here when added to a day's queue.</>
          )}
        </div>
      ) : (
        <div className="space-y-5" data-testid="bank-groups">
          {groups.map((g) => (
            <div key={g.job} data-testid={`bank-group-${g.job}`} className="bg-[#16181A] border border-[#2E3033]">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#2E3033]">
                <button
                  data-testid={`bank-job-priority-${g.job}`}
                  onClick={() => toggleJob(g.job, tab, !g.priority)}
                  title={g.priority ? "Unmark job priority" : "Mark whole job as priority"}
                  className={`transition-colors ${g.priority ? "text-[#F59E0B]" : "text-[#6E7787] hover:text-[#F59E0B]"}`}
                >
                  <Star className="w-4 h-4" fill={g.priority ? "#F59E0B" : "none"} />
                </button>
                <span className="font-heading font-bold text-white text-sm tracking-[0.12em]">{g.job}</span>
                {g.priority && (
                  <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B]">
                    Priority
                  </span>
                )}
                <span className="text-[10px] font-mono text-[#6E7787] ml-auto">{g.items.length} panel{g.items.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
                {g.items.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`bank-panel-${p.id}`}
                    className={`flex items-stretch bg-[#0A0A0A] border transition-colors ${p.priority ? "border-[#F59E0B]/50" : "border-[#2E3033]"}`}
                  >
                    <div className="w-1.5 shrink-0" style={{ backgroundColor: p.color }} />
                    <div className="flex-1 p-2.5 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <button
                          data-testid={`bank-panel-priority-${p.id}`}
                          onClick={() => togglePanel(p)}
                          title={p.priority ? "Unmark priority" : "Mark priority"}
                          className={`shrink-0 transition-colors ${p.priority ? "text-[#F59E0B]" : "text-[#6E7787] hover:text-[#F59E0B]"}`}
                        >
                          <Star className="w-3.5 h-3.5" fill={p.priority ? "#F59E0B" : "none"} />
                        </button>
                        <span className="font-heading font-bold text-white text-sm break-words leading-tight">{p.name}</span>
                        <div className="flex items-center gap-1.5 ml-auto shrink-0">
                          {p.drawingId && (
                            <button data-testid={`bank-view-${p.id}`} onClick={() => setViewPanel(p)} className="text-[#6E7787] hover:text-[#10B981]" title="View drawing">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {tab === "active" ? (
                            <button data-testid={`bank-complete-${p.id}`} onClick={() => movePanel(p, "completed")} className="text-[#6E7787] hover:text-[#10B981]" title="Move to Completed">
                              <PackageCheck className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button data-testid={`bank-restore-${p.id}`} onClick={() => movePanel(p, "active")} className="text-[#6E7787] hover:text-[#2560C9]" title="Restore to Drawing Bank">
                              <Undo2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button data-testid={`bank-delete-${p.id}`} onClick={() => removePanel(p)} className="text-[#6E7787] hover:text-[#EF4444]" title="Delete from bank">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1.5 text-[10px] font-mono text-[#A0AABF] uppercase tracking-wide">
                        {fmt(p.width)} × {fmt(p.length)} m · {fmt(p.volume, 2)} m³ · {fmt(p.mass / 1000, 2)} t
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewPanel && (
        <div data-testid="bank-drawing-modal" onClick={() => setViewPanel(null)} className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-[#16181A] border border-[#2E3033]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] shrink-0">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5" style={{ backgroundColor: viewPanel.color }} />
                <h3 className="font-heading font-bold text-white text-sm">{viewPanel.name}</h3>
                <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 border border-[#2560C9]/50 text-[#3B75DF]">{viewPanel.jobNumber}</span>
              </div>
              <div className="flex items-center gap-3">
                <a href={`${API}/panels/drawing/${viewPanel.drawingId}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#A0AABF] hover:text-white text-xs font-mono">
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <button onClick={() => setViewPanel(null)} className="text-[#6E7787] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#0A0A0A] p-3">
              <img src={`${API}/panels/drawing/${viewPanel.drawingId}`} alt={viewPanel.name} className="w-full h-auto bg-white" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BankTab({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 border transition-colors ${
        active ? "bg-[#2560C9] border-[#2560C9] text-white" : "border-[#2E3033] text-[#A0AABF] hover:bg-[#22252A]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}
